/**
 * The social networks an organisation profile can display.
 *
 * MIRRORS THE SERVER'S ALLOWLIST (`Organization.social_keys`). The server is
 * the authority — it rejects anything else at the changeset — and this exists
 * so the client offers a picker rather than a free-text field nobody can
 * validate. If the two drift, the server wins and the client shows a network
 * the user cannot save; keep them in step.
 */
export const SOCIAL_NETWORKS = {
  x: { label: 'X', placeholder: 'https://x.com/…' },
  linkedin: { label: 'LinkedIn', placeholder: 'https://linkedin.com/company/…' },
  github: { label: 'GitHub', placeholder: 'https://github.com/…' },
  youtube: { label: 'YouTube', placeholder: 'https://youtube.com/@…' },
  instagram: { label: 'Instagram', placeholder: 'https://instagram.com/…' },
  facebook: { label: 'Facebook', placeholder: 'https://facebook.com/…' },
  mastodon: { label: 'Mastodon', placeholder: 'https://mastodon.social/@…' },
  bluesky: { label: 'Bluesky', placeholder: 'https://bsky.app/profile/…' },
};

export const SOCIAL_KEYS = Object.keys(SOCIAL_NETWORKS);

/**
 * A socials map as an ordered list of {key, url}, in the order defined above.
 *
 * Fixed order, not object order: a profile whose links rearrange themselves
 * between renders — or between the in-app page and the public one — reads as
 * broken. Unknown keys are dropped, matching the server, which drops them too.
 */
export function socialEntries(socials) {
  const map = socials || {};
  return SOCIAL_KEYS.filter((k) => map[k]).map((k) => ({ key: k, url: map[k] }));
}

/**
 * Is this a URL the server will accept?
 *
 * Client-side for FEEDBACK only — the changeset makes the decision, and it
 * applies the same rule (http/https with a real host). Anything stricter here
 * would reject values the server accepts; anything looser just means the error
 * arrives from the server instead.
 */
export function isAcceptableUrl(value) {
  if (!value) return true;
  const raw = String(value).trim();

  // `https:///x` is the one place the two parsers genuinely disagree, and it
  // took a test to find: the browser's URL normalises the empty authority away
  // and reports the host as "x", while Elixir's URI.parse reports an empty
  // host and the changeset rejects it. Caught here so the client does not
  // accept a value the server will refuse.
  if (/^[a-z][a-z0-9+.-]*:\/\/\//i.test(raw)) return false;

  try {
    const url = new URL(raw);
    return (url.protocol === 'http:' || url.protocol === 'https:') && !!url.hostname;
  } catch {
    return false;
  }
}
